import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowUpRight, CheckCircle2, ChevronDown, Leaf, Phone, Mail, X, XCircle } from 'lucide-react';
import axios from 'axios';
import { QUOTE_PRODUCT_OPTIONS } from '../../data/quoteProductOptions';
import styles from './CTASection.module.css';

const CATALOG_QR_URL = 'https://www.greeneripapers.com/catalog/greeneripapers-catalog.pdf';
const QR_LOGO_SRC = '/apple-touch-icon.png';

const CTASection = ({
  title = "Ready to Transition to Sustainable Packaging?",
  subtitle = "Get certified food-grade, greaseproof, and 100% compostable paper wraps customized for your brand.",
  phone = "+91 9557005492 , 9759599922",
  email = "info@greeneripapers.com",
  onQuoteSubmit
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [openDropdown, setOpenDropdown] = useState('');
  const [pageToast, setPageToast] = useState('');
  const feedbackRef = useRef(null);
  const productSelectRef = useRef(null);
  const monthlySelectRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    productType: '',
    monthlyRequirement: '',
    message: '',
    consent: false,
  });
  const productOptions = QUOTE_PRODUCT_OPTIONS;
  const quantityOptions = ['Under 1000 kgs', '2 - 5 tons', 'Above 5 tons'];

  const isFormValid = useMemo(
    () =>
      formData.fullName.trim() &&
      formData.email.trim() &&
      formData.phone.trim() &&
      formData.productType.trim() &&
      formData.monthlyRequirement &&
      formData.consent,
    [formData]
  );

  useEffect(() => {
    document.body.style.overflow = isFormOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFormOpen]);

  useEffect(() => {
    if (!openDropdown) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const insideProduct = productSelectRef.current?.contains(event.target);
      const insideMonthly = monthlySelectRef.current?.contains(event.target);
      if (!insideProduct && !insideMonthly) {
        setOpenDropdown('');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openDropdown]);

  useEffect(() => {
    if (!isFormOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsFormOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFormOpen]);

  const handleCatalogDownload = () => {
    const catalogPath = "/catalog/greeneripapers-catalog.pdf";
    const link = document.createElement('a');
    link.href = catalogPath;
    link.download = "greeneripapers-catalog.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleQuoteSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      if (onQuoteSubmit) {
        await onQuoteSubmit(formData);
      } else {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
        await axios.post(`${apiUrl}/api/quote`, {
          fullName: formData.fullName,
          companyName: formData.companyName,
          businessEmail: formData.email,
          phoneNumber: formData.phone,
          productType: formData.productType,
          monthlyRequirement: formData.monthlyRequirement,
          requirementNotes: formData.message,
          consent: formData.consent
        });
      }

      setSubmitStatus('success');
      setSubmitMessage(
        'Thank you! Your quote request has been sent successfully. Our team will contact you within 24 hours.'
      );
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        productType: '',
        monthlyRequirement: '',
        message: '',
        consent: false,
      });
      setOpenDropdown('');
    } catch (error) {
      console.error('Quote submit failed:', error);
      setSubmitStatus('error');
      setSubmitMessage('Unable to submit your quote request right now. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeQuoteModal = () => {
    setIsFormOpen(false);
    setSubmitStatus('');
    setSubmitMessage('');
    setOpenDropdown('');
  };

  const openQuoteModal = () => {
    setSubmitMessage('');
    setSubmitStatus('');
    setPageToast('');
    setIsFormOpen(true);
  };

  useEffect(() => {
    if (submitStatus !== 'success' || !isFormOpen) {
      return undefined;
    }

    const closeTimer = setTimeout(() => {
      setPageToast('Quote request sent successfully. Our team will contact you shortly.');
      closeQuoteModal();
    }, 2500);

    return () => clearTimeout(closeTimer);
  }, [submitStatus, isFormOpen]);

  useEffect(() => {
    if (!pageToast) {
      return undefined;
    }

    const hideTimer = setTimeout(() => setPageToast(''), 4500);
    return () => clearTimeout(hideTimer);
  }, [pageToast]);

  const renderTitle = () => {
    if (title.includes('Sustainable Packaging')) {
      return (
        <>
          Ready to Transition to{' '}
          <span className={styles.headingAccent}>Sustainable Packaging?</span>
        </>
      );
    }
    return title;
  };

  return (
    <section id="product-quote" className={styles.ctaContainer}>
      <div className={styles.bgDecor} aria-hidden="true">
        <div className={styles.topGlow} />
        <div className={styles.meshGrid} />
        <span className={styles.watermark}>GREENERI</span>
        <div className={styles.orbGreen} />
        <div className={styles.orbOrange} />
        <div className={styles.topAccentLine} />
      </div>

      <div className={styles.wrapper}>
        <div className={styles.glassCard}>
          <div className={styles.textSide}>
            <div className={styles.badge}>
              <Leaf size={12} aria-hidden="true" />
              <span>100% Eco-Friendly &amp; FDA Approved</span>
            </div>

            <h2 className={styles.heading}>{renderTitle()}</h2>
            <p className={styles.subtext}>{subtitle}</p>

            <ul className={styles.trustRow}>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                Custom GSM &amp; sizes
              </li>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                Pan-India dispatch
              </li>
              <li>
                <CheckCircle2 size={14} aria-hidden="true" />
                Bulk B2B pricing
              </li>
            </ul>

            <div className={styles.quickContacts}>
              <a href={`tel:${phone.split(',')[0].trim()}`} className={styles.contactLink}>
                <Phone size={14} aria-hidden="true" />
                <span>{phone}</span>
              </a>
              <a href={`mailto:${email}`} className={styles.contactLink}>
                <Mail size={14} aria-hidden="true" />
                <span>{email}</span>
              </a>
            </div>

            <div className={styles.actionButtons}>
              <button
                className={styles.primaryBtn}
                onClick={openQuoteModal}
                type="button"
              >
                <span>Get Custom Quote</span>
                <ArrowUpRight size={18} aria-hidden="true" />
              </button>
              <a
                href="/catalog.pdf"
                className={styles.secondaryBtn}
                onClick={(event) => {
                  event.preventDefault();
                  handleCatalogDownload();
                }}
              >
                Download Catalog
              </a>
            </div>
          </div>

          <div className={styles.qrSide}>
            <div className={styles.qrPanel}>
              <span className={styles.qrBadge}>Scan &amp; Connect</span>
              <div className={styles.qrCodeWrapper}>
                <QRCodeSVG
                  value={CATALOG_QR_URL}
                  title="Scan to download Greeneri product catalog"
                  size={168}
                  level="H"
                  marginSize={2}
                  bgColor="#ffffff"
                  fgColor="#1b8b3d"
                  imageSettings={{
                    src: QR_LOGO_SRC,
                    height: 36,
                    width: 36,
                    excavate: true,
                  }}
                  className={styles.qrCode}
                  role="img"
                  aria-label="QR code to download product catalog"
                />
              </div>
              <p className={styles.qrSubtext}>Download catalog or share your requirement instantly</p>
            </div>
          </div>
        </div>
      </div>

      {pageToast && (
        <div className={styles.pageToast} role="status" aria-live="polite">
          <CheckCircle2 size={22} />
          <span>{pageToast}</span>
        </div>
      )}

      {isFormOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => submitStatus !== 'success' && closeQuoteModal()}
          role="presentation"
        >
          <div
            className={`${styles.modalCard} ${submitStatus === 'success' ? styles.modalCardSuccess : ''}`}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Custom Quote Request Form"
          >
            {submitStatus !== 'success' && (
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeQuoteModal}
                aria-label="Close quote form"
              >
                <X size={18} />
              </button>
            )}

            {submitStatus === 'success' ? (
              <div className={styles.successModalOnly} role="status" aria-live="polite">
                <div className={styles.successIconWrap}>
                  <CheckCircle2 size={56} strokeWidth={2.2} />
                </div>
                <h3 className={styles.successTitle}>Quote Sent Successfully!</h3>
                <p className={styles.successText}>
                  Thank you for your enquiry. Our sales team will contact you shortly with pricing
                  and product details.
                </p>
                <p className={styles.successHint}>Expected response: within 24 hours</p>
            
              </div>
            ) : (
            <div className={styles.modalContent}>
              <div className={styles.modalInfo}>
                <span className={styles.modalBadge}>Quick B2B Quote</span>
                <h3 className={styles.formTitle}>Get Your Best Price in Minutes</h3>
                <p className={styles.formSubtitle}>
                  Fill only key details. Our team will call or email with the best quote and samples.
                </p>
                <div className={styles.highlights}>
                  <p>Fast response for bulk requirements</p>
                  <p>Custom GSM and coating options</p>
                  <p>PAN India dispatch support</p>
                </div>
              </div>

              <form className={styles.quoteForm} onSubmit={handleQuoteSubmit}>
                {submitStatus === 'error' && submitMessage && (
                  <div
                    ref={feedbackRef}
                    className={`${styles.feedbackBanner} ${styles.feedbackError}`}
                    role="alert"
                  >
                    <XCircle size={32} className={styles.feedbackIconLarge} />
                    <div>
                      <p className={styles.feedbackTitle}>Submission Failed</p>
                      <p className={styles.feedbackText}>{submitMessage}</p>
                    </div>
                  </div>
                )}

                <div className={styles.formGrid}>
                  <label className={styles.inputGroup}>
                    <span>
                      Full Name<span className={styles.requiredMark}>*</span>
                    </span>
                    <input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      required
                    />
                  </label>

                  <label className={styles.inputGroup}>
                    <span>Company Name</span>
                    <input
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Your company (optional)"
                    />
                  </label>

                  <label className={styles.inputGroup}>
                    <span>
                      Business Email<span className={styles.requiredMark}>*</span>
                    </span>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="name@company.com"
                      required
                    />
                  </label>

                  <label className={styles.inputGroup}>
                    <span>
                      Phone Number<span className={styles.requiredMark}>*</span>
                    </span>
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91"
                      required
                    />
                  </label>

                  <label className={styles.inputGroup}>
                    <span>
                      Product Type<span className={styles.requiredMark}>*</span>
                    </span>
                    <div
                      ref={productSelectRef}
                      className={styles.customSelect}
                    >
                      <button
                        type="button"
                        className={`${styles.selectTrigger} ${
                          openDropdown === 'productType' ? styles.selectTriggerOpen : ''
                        }`}
                        onClick={() =>
                          setOpenDropdown((prev) => (prev === 'productType' ? '' : 'productType'))
                        }
                      >
                        <span>{formData.productType || 'Select product'}</span>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'productType' && (
                        <ul className={styles.selectMenu}>
                          {productOptions.map((option) => (
                            <li key={option}>
                              <button
                                type="button"
                                className={`${styles.selectOption} ${
                                  formData.productType === option ? styles.selectOptionActive : ''
                                }`}
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, productType: option }));
                                  setOpenDropdown('');
                                }}
                              >
                                {option}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </label>

                  <label className={styles.inputGroup}>
                    <span>
                      Monthly Requirement<span className={styles.requiredMark}>*</span>
                    </span>
                    <div
                      ref={monthlySelectRef}
                      className={styles.customSelect}
                    >
                      <button
                        type="button"
                        className={`${styles.selectTrigger} ${
                          openDropdown === 'monthlyRequirement' ? styles.selectTriggerOpen : ''
                        }`}
                        onClick={() =>
                          setOpenDropdown((prev) =>
                            prev === 'monthlyRequirement' ? '' : 'monthlyRequirement'
                          )
                        }
                      >
                        <span>{formData.monthlyRequirement || 'Select quantity range'}</span>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'monthlyRequirement' && (
                        <ul className={styles.selectMenu}>
                          {quantityOptions.map((option) => (
                            <li key={option}>
                              <button
                                type="button"
                                className={`${styles.selectOption} ${
                                  formData.monthlyRequirement === option
                                    ? styles.selectOptionActive
                                    : ''
                                }`}
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, monthlyRequirement: option }));
                                  setOpenDropdown('');
                                }}
                              >
                                {option}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </label>
                </div>

                <label className={styles.inputGroup}>
                  <span>Requirement Notes</span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Optional: dimensions, GSM preference, printing, delivery city."
                  />
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    required
                  />
                <span>
                  I agree to be contacted for this quote.
                  <span className={styles.requiredMark}>*</span>
                </span>
                </label>

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Request Quote'}
                  </button>
                  <p className={styles.formFootNote}>Expected response time: within 24 hours</p>
                </div>
              </form>
            </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CTASection;